/**
 * Styleboards Popup Component
 *
 * A web component that powers the dynamic styleboards popup in the header.
 * Fetches styleboards on trigger click and displays appropriate UI for guests vs users.
 */

import { StyleboardSelectorDialog } from '@theme/styleboard-selector-modal';

const DEFAULT_API_BASE_URL = `https://${window.location.host}/apps/jyapi`;

/**
 * Gets the notification function
 * @returns {{ success: Function, error: Function, info: Function } | null}
 */
function getNotification() {
  return /** @type {any} */ (window).notification || null;
}

/**
 * Backward-compatible token attachment.
 * Supports both new and older selector modal builds.
 * @param {URL} url
 */
function applyStyleboardTokens(url) {
  if (typeof StyleboardSelectorDialog?.applySessionTokensToUrl === 'function') {
    StyleboardSelectorDialog.applySessionTokensToUrl(url);
    return;
  }

  const sessionToken =
    typeof StyleboardSelectorDialog?.getSessionToken === 'function'
      ? StyleboardSelectorDialog.getSessionToken()
      : null;

  if (!sessionToken) return;

  if (url.pathname.includes('/retail/styleboards-list')) {
    url.searchParams.append('ids[]', sessionToken);
    return;
  }

  url.searchParams.set('session_token', sessionToken);
}

class StyleboardsPopup extends HTMLElement {
  /** @type {HTMLElement | null} */
  #contentElement = null;

  /** @type {boolean} */
  #isLoading = false;

  /** @type {boolean} */
  #hasLoaded = false;

  /** @type {Array<{ id: string, name: string, items_count?: number }>} */
  #styleboards = [];

  /** @type {((event: Event) => void) | null} */
  #createButtonClickHandler = null;

  get apiBaseUrl() {
    return Theme.api?.baseUrl || DEFAULT_API_BASE_URL;
  }

  connectedCallback() {
    this.#contentElement = this.querySelector('.popup__content');

    // Listen for popup open events
    this.addEventListener('popup:show', this.#handlePopupShow);

    // Listen for styleboard changes to refresh
    document.addEventListener('product-added-to-styleboard', this.#handleStyleboardChange);

    const createButton = this.querySelector('.popup__button');
    if (createButton instanceof HTMLButtonElement) {
      this.#createButtonClickHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        void this.#openCreateDialog();
      };
      createButton.addEventListener('click', this.#createButtonClickHandler);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('popup:show', this.#handlePopupShow);
    document.removeEventListener('product-added-to-styleboard', this.#handleStyleboardChange);

    const createButton = this.querySelector('.popup__button');
    if (createButton instanceof HTMLButtonElement && this.#createButtonClickHandler) {
      createButton.removeEventListener('click', this.#createButtonClickHandler);
    }
    this.#createButtonClickHandler = null;
  }

  /**
   * Handle popup show event to load styleboards
   * @param {CustomEvent} event
   */
  #handlePopupShow = (event) => {
    // Load styleboards if not already loaded
    if (!this.#hasLoaded && !this.#isLoading) {
      this.#loadStyleboards();
    }
  };

  /**
   * Handle styleboard change events
   */
  #handleStyleboardChange = () => {
    // Refresh on next open
    this.#hasLoaded = false;
  };

  async #openCreateDialog() {
    try {
      await customElements.whenDefined('styleboard-selector-dialog');
    } catch {
      // Ignore and attempt to find element anyway.
    }

    const dialog =
      /** @type {any} */ (document.getElementById('styleboard-selector-dialog')) ||
      document.querySelector('styleboard-selector-dialog');

    if (!dialog || typeof dialog.showCreateMode !== 'function') {
      const notify = getNotification();
      if (notify?.error) notify.error('Unable to create styleboard', 'Styleboard dialog is unavailable');
      return;
    }

    dialog.showCreateMode();
  }

  /**
   * Builds API URL with session token for guest users
   * @param {string} endpoint
   * @returns {string}
   */
  #buildApiUrl(endpoint) {
    const url = new URL(`${this.apiBaseUrl}${endpoint}`, /^https?:\/\//i.test(this.apiBaseUrl) ? undefined : window.location.origin);
    applyStyleboardTokens(url);
    return url.toString();
  }

  /**
   * Fetches styleboards from the API
   */
  async #loadStyleboards() {
    if (!this.#contentElement) return;

    this.#isLoading = true;
    this.#renderLoading();

    try {
      const response = await fetch(this.#buildApiUrl('/retail/styleboards-list'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch styleboards');
      }

      const data = await response.json();
      this.#styleboards = data.data?.styleboards || [];
      this.#hasLoaded = true;
      this.#renderStyleboards();
    } catch (error) {
      console.error('Error loading styleboards:', error);
      this.#renderError();
    } finally {
      this.#isLoading = false;
    }
  }

  /**
   * Renders loading state
   */
  #renderLoading() {
    if (!this.#contentElement) return;

    const existingList = this.#contentElement.querySelector('.popup__styleboards-list');
    if (existingList) {
      existingList.innerHTML = '<div class="popup__loading">Loading...</div>';
    }
  }

  /**
   * Renders error state
   */
  #renderError() {
    if (!this.#contentElement) return;

    const existingList = this.#contentElement.querySelector('.popup__styleboards-list');
    if (existingList) {
      existingList.innerHTML = '<div class="popup__error">Unable to load styleboards</div>';
    }
  }

  /**
   * Renders the styleboards list
   */
  #renderStyleboards() {
    if (!this.#contentElement) return;

    // Find or create the list container
    let listContainer = this.#contentElement.querySelector('.popup__styleboards-list');
    if (!listContainer) {
      listContainer = document.createElement('div');
      listContainer.className = 'popup__styleboards-list';
      // Insert before the button
      const button = this.#contentElement.querySelector('.popup__button');
      if (button) {
        button.before(listContainer);

      } else {
        this.#contentElement.appendChild(listContainer);
      }
    }

    if (this.#styleboards.length === 0) {
      listContainer.innerHTML = `
        <p class="popup__empty-message">You don't have any styleboards yet.</p>
      `;
      // Update button to "Create Board"
      const button = this.#contentElement.querySelector('.popup__button');
      if (button) {
        button.textContent = 'CREATE BOARD';
      }
      return;
    }

    // Render styleboard list
    const itemsHtml = this.#styleboards.map(board => {
      const viewUrl = `/apps/jyapi/retail/styleboards/${board.id}`;
      const itemCount = this.#getStyleboardItemCount(board);

      return `
        <a href="${viewUrl}" class="popup__styleboard-item">
          <span class="popup__styleboard-name">${this.#escapeHtml(board.name)}</span>
          <span class="popup__styleboard-count">${itemCount} item${itemCount !== 1 ? 's' : ''}</span>
        </a>
      `;
    }).join('');

    listContainer.innerHTML = itemsHtml;
    const button = this.#contentElement.querySelector('.popup__button');
    if (button) {
      button.textContent = 'CREATE NEW BOARD';
    }
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Derives item count from various API shapes.
   * The list endpoint may return counts as `itemsCount`, `items_count`, `item_count`,
   * or omit counts and only include an `items` array.
   *
   * @param {any} board
   * @returns {number}
   */
  #getStyleboardItemCount(board) {
    const candidates = [
      board?.items_count,
      board?.itemsCount,
      board?.item_count,
      board?.itemCount,
    ];

    for (const value of candidates) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) {
        return Math.max(0, Number(value));
      }
    }

    const items = board?.items ?? board?.styleboard?.items ?? null;
    if (Array.isArray(items)) return items.length;
    if (items && typeof items === 'object' && Array.isArray(items.data)) return items.data.length;

    return 0;
  }
}

if (!customElements.get('styleboards-popup')) {
  customElements.define('styleboards-popup', StyleboardsPopup);
}

export { StyleboardsPopup };
